import { isRecord } from './util.js';
/**
 * The maker's order and old names (docs/superpowers/specs/2026-10-16-world-order-and-renames-design.md).
 *
 * Order: a package is stored as `jsonb`, and Postgres sorts an object's keys (shortest first, then
 * bytewise), so `features` and each feature's `states` lose the order of `wereld.json`. The platform
 * records it at upload as `order` (arrays survive `jsonb`); `orderedFeatures` and `orderedStates` are
 * the only way to list them. A package without `order` (stored before it) lists them as its object has
 * them, as before.
 *
 * Old names: a feature's `eerder` (`{ old state: current state }`) and the package's
 * `eerdereOnderdelen` (`{ old feature: current feature }`) let a lesson written for an earlier version
 * keep working: the lesson rules, the headset and the publish check read an old name as the current one.
 */
/** Names in a recorded order: the recorded ones that exist, then any others as the object has them. */
function inOrder(recorded, names) {
    if (!Array.isArray(recorded))
        return [...names];
    const have = new Set(names);
    const out = [];
    for (const name of recorded) {
        if (typeof name === 'string' && have.has(name) && !out.includes(name))
            out.push(name);
    }
    for (const name of names)
        if (!out.includes(name))
            out.push(name);
    return out;
}
/** The package's features' keys, in the maker's order (the recorded `order`, else the object's). */
export function orderedFeatures(pkg) {
    return inOrder(pkg.order?.features, Object.keys(pkg.features));
}
/** A feature's states, in the maker's order; none for a feature the package does not have. */
export function orderedStates(pkg, feature) {
    if (!Object.hasOwn(pkg.features, feature))
        return [];
    const states = Object.keys(pkg.features[feature].states);
    const recorded = pkg.order?.states;
    return inOrder(isRecord(recorded) && Object.hasOwn(recorded, feature) ? recorded[feature] : undefined, states);
}
/**
 * The order of `features` and their `states` as this object has them. For a package just read from
 * `wereld.json` that is the file's order: JSON.parse keeps the order of keys that are not array
 * indices, and a feature's or state's name starts with a letter (the format's rule).
 */
export function packageOrder(pkg) {
    const features = Object.keys(pkg.features);
    return {
        features,
        states: Object.fromEntries(features.map((name) => [name, Object.keys(pkg.features[name].states)])),
    };
}
/** The package with `order` recorded from its own objects' order (at upload, from `wereld.json`). */
export function withPackageOrder(pkg) {
    return { ...pkg, order: packageOrder(pkg) };
}
/**
 * The package with `features` and each feature's `states` rebuilt in the maker's order and `order`
 * left out: a reader that lists by the object's own order (JSON over the wire keeps it), such as a
 * headset from before `order`, lists them as the maker meant.
 */
export function inPackageOrder(pkg) {
    const { order: _order, ...rest } = pkg;
    const features = Object.fromEntries(orderedFeatures(pkg).map((name) => {
        const feature = pkg.features[name];
        const states = Object.fromEntries(orderedStates(pkg, name).map((state) => [state, feature.states[state]]));
        return [name, { ...feature, states }];
    }));
    return { ...rest, features };
}
/**
 * Names in the order `jsonb` stores an object's keys: shorter first, then bytewise (a feature's or
 * state's name is ASCII, so by character). What a package stored before `order` listed, kept where a
 * prompt of before must stay byte for byte.
 */
export function storedKeyOrder(names) {
    return [...names].sort((a, b) => a.length !== b.length ? a.length - b.length : a < b ? -1 : a > b ? 1 : 0);
}
/** The package's old names (`eerdereOnderdelen`, each feature's `eerder`); empty maps when none. */
export function packageAliases(pkg) {
    const features = {};
    for (const [old, now] of Object.entries(pkg.eerdereOnderdelen ?? {})) {
        Object.defineProperty(features, old, {
            value: now,
            enumerable: true,
            writable: true,
            configurable: true,
        });
    }
    const states = {};
    for (const [name, feature] of Object.entries(pkg.features)) {
        const old = feature.eerder;
        if (old === undefined || Object.keys(old).length === 0)
            continue;
        const map = {};
        for (const [was, now] of Object.entries(old)) {
            Object.defineProperty(map, was, {
                value: now,
                enumerable: true,
                writable: true,
                configurable: true,
            });
        }
        Object.defineProperty(states, name, {
            value: map,
            enumerable: true,
            writable: true,
            configurable: true,
        });
    }
    return { features, states };
}
export const hasAliases = (aliases) => Object.keys(aliases.features).length > 0 || Object.keys(aliases.states).length > 0;
/**
 * The current name of a feature a lesson names: itself when the world has it, else what an old name
 * now is; undefined when neither.
 */
export function currentFeature(features, aliases, name) {
    if (Object.hasOwn(features, name))
        return name;
    const now = aliases !== undefined && Object.hasOwn(aliases.features, name)
        ? aliases.features[name]
        : undefined;
    return now !== undefined && Object.hasOwn(features, now) ? now : undefined;
}
/**
 * The current name of a state a lesson names, of a feature by its current name: itself when the
 * feature has it, else what an old name now is; undefined when neither.
 */
export function currentState(states, aliases, feature, state) {
    if (states.includes(state))
        return state;
    const old = aliases !== undefined && Object.hasOwn(aliases.states, feature)
        ? aliases.states[feature]
        : undefined;
    const now = old !== undefined && Object.hasOwn(old, state) ? old[state] : undefined;
    return now !== undefined && states.includes(now) ? now : undefined;
}
/**
 * Names the new version uses as current ones that the version before kept as old names (`eerder`,
 * `eerdereOnderdelen`), in Dutch: allowed -- an old name may be used again -- but from then on a lesson
 * naming it plays the new feature or state, no longer the one it was an old name for. The platform
 * warns at a new version's upload and on its item page; the kit cannot know the version before.
 */
export function reusedOldNames(previous, next) {
    const was = packageAliases(previous);
    const out = [];
    for (const feature of orderedFeatures(next)) {
        if (Object.hasOwn(was.features, feature) && !Object.hasOwn(previous.features, feature))
            out.push(`Het onderdeel "${feature}" was in de vorige versie een oude naam voor "${was.features[feature]}": een les die "${feature}" noemt, speelt nu dit nieuwe onderdeel.`);
    }
    for (const feature of orderedFeatures(next)) {
        const old = Object.hasOwn(was.states, feature) ? was.states[feature] : undefined;
        if (old === undefined)
            continue;
        for (const state of orderedStates(next, feature)) {
            if (Object.hasOwn(old, state))
                out.push(`De toestand "${state}" van "${feature}" was in de vorige versie een oude naam voor "${old[state]}": een les die "${state}" noemt, speelt nu deze nieuwe toestand.`);
        }
    }
    return out;
}
//# sourceMappingURL=order.js.map